import { useMemo, useState } from 'react';
import { useCurrentRefinements, useHits } from 'react-instantsearch-hooks';
import BooleanFacet from '../components/facets/boolean';
import ColorFacet from '../components/facets/color';
import RangeFacet from '../components/facets/range';
import TermFacet from '../components/facets/term';
import { FacetProps } from '../components/facets/types';
import { FacetConfiguration } from '../types';
import usePath from '@/hooks/usePath';
import {
  isLimitedFacetListingPage,
  resolveCategorySlugFromPathSegments,
} from '@/lib/tastics/product-list/categoryFacetsConfiguration';
interface Options {
  configuration?: Record<string, FacetConfiguration>;
  ordering?: string[];
  render?: (result: { attribute: string; Component: JSX.Element }) => JSX.Element;
}
 
const useDynamicFacets = ({ configuration, ordering, render }: Options = {}) => {
  const path = usePath();
  const { items } = useCurrentRefinements();
  const pathSegments = path.path.split('/').filter(Boolean);
  const categorySlugFromPath = pathSegments.at(-1);
  const hasCategoryInPath = Boolean(resolveCategorySlugFromPathSegments(pathSegments));
  const isLimitedFacetPage = isLimitedFacetListingPage(path.path, categorySlugFromPath);
  const visibleItemsCount = items.length > 0 || hasCategoryInPath ? 9999 : 8; 

  const handleFacetSelect = (attribute: string) => {
    setSelectedFilters((prev) => {
      const updated = new Set(prev);
      if (updated.has(attribute)) {
        updated.delete(attribute);
      } else {
        updated.add(attribute);
      }
      return updated;
    });
  };


  const { results } = useHits();
  const [selectedFilters, setSelectedFilters] = useState<Set<string>>(new Set()); // Track selected filters

  const facetMapping = useMemo<Record<FacetConfiguration['type'], React.ComponentType<FacetProps>>>(
    () => ({
      range: RangeFacet,
      color: ColorFacet,
      term: TermFacet,
      boolean: BooleanFacet,
    }),
    []
  );
 
  const dynamicFacets = useMemo(() => {
    if (!configuration) return <></>;
 
    const facets = Object.keys(configuration).map((attribute) => {
      
      const facet = configuration[attribute];
      const disjunctiveFacet = results?.disjunctiveFacets?.find((f) => f.name === attribute);
      const hasData = disjunctiveFacet && Object.keys(disjunctiveFacet?.data ?? {}).length > 0;
      const isEmptyFacet =
        (facet?.type === 'color' || facet?.type === 'term' || facet?.type === 'boolean' || facet?.type === 'range') &&
        !hasData;
      const Component = facetMapping[facet?.type];
      const FinalComponent = <Component key={attribute} label={facet?.label} attribute={attribute} />;
 
      return {
        attribute,
        Component: (
          <div
            key={attribute}
            className={isEmptyFacet ? 'hidden' : 'block'}
            onClick={() => handleFacetSelect(attribute)} // Handle facet selection here
          >
            {render?.({ attribute, Component: FinalComponent }) ?? FinalComponent}
          </div>
        ),
      };
    });
 
    if (ordering) {
      facets.sort((a, b) => ordering.indexOf(a?.attribute) - ordering.indexOf(b?.attribute));
    }
 
    if (isLimitedFacetPage) {
      return facets.slice(0, visibleItemsCount).map(facet => facet?.Component);
    }
    
    return facets.map(facet => facet?.Component);
  }, [
    facetMapping,
    configuration,
    ordering,
    render,
    results?.disjunctiveFacets,
    selectedFilters,
    path.path,
    items.length,
    hasCategoryInPath,
    isLimitedFacetPage,
    categorySlugFromPath,
  ]);
 
  return dynamicFacets;
};
 
export default useDynamicFacets;